import React, { useEffect, useRef, useState } from "react";
import Button from "./Button";
import FA_LANG from "@/assets/locale/fa";
import icons from "@/assets/icons";

type ImageUploaderProps = {
  handler?: (image: any) => void;
  videoUrl?: string;
  error?: boolean;
  errorMessage?: string;
  editable: boolean;
  width: string;
  height: string;
  uniqueId?: string;
  autoplay?: boolean;
  playFinished?: () => void;
  controls?: boolean;
};

const VideoUploader: React.FC<ImageUploaderProps> = ({
  handler,
  videoUrl,
  error,
  errorMessage,
  editable,
  width,
  height,
  uniqueId,
  autoplay,
  playFinished,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);

  function onVideoChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (handler) handler(e.target?.files?.[0]);
  }

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  // useEffect(() => {
  //   if (autoplay && videoRef.current) {
  //     const playVideo = async () => {
  //       try {
  //         await videoRef.current.play();
  //         setIsPlaying(true);
  //       } catch (error) {
  //         console.warn(
  //           "Autoplay failed. The user may need to interact with the page first."
  //         );
  //       }
  //     };
  //     playVideo();
  //   }
  // }, [autoplay, videoRef.current]);

  useEffect(() => {
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement.addEventListener("play", handlePlay);
      videoElement.addEventListener("pause", handlePause);
    }

    return () => {
      if (videoElement) {
        videoElement.removeEventListener("play", handlePlay);
        videoElement.removeEventListener("pause", handlePause);
      }
    };
  }, []);

  const resetVideo = () => {
    if (handler) handler(undefined);
  };

  const setFinished = () => {
    setIsPlaying(false);
    if (playFinished) playFinished();
  };
  const handleFullscreen = () => {
    if (videoRef.current) {
      const videoElement = videoRef.current as any; // Type assertion for non-standard properties

      if (videoElement.requestFullscreen) {
        videoElement.requestFullscreen();
      } else if (videoElement.webkitRequestFullscreen) {
        // Safari compatibility
        videoElement.webkitRequestFullscreen();
      } else if (videoElement.msRequestFullscreen) {
        // IE compatibility
        videoElement.msRequestFullscreen();
      } else {
        console.warn("Fullscreen API is not supported by this browser.");
      }
    }
  };
  return (
    <>
      <div className="w-full h-full">
        <div
          className={`inline-flex flex-col items-center rounded-2xl border-2 ${
            videoUrl ? "border-none" : "border-dashed"
          } border-bg-cards2 bg-gray-0 pt-20 relative ${width} ${height}`}
        >
          {!videoUrl ? (
            <div className="inline-flex flex-col items-center self-center image-upload">
              <label
                htmlFor={uniqueId}
                className="inline-flex flex-col items-center gap-3 text-text-caption2 text-[10px]"
              >
                <div className="w-16 h-16">{icons.upload}</div>
                {FA_LANG.SELECT_VIDEO}
              </label>
              <input id={uniqueId} type="file" onChange={onVideoChange} />
            </div>
          ) : (
            editable && (
              <div>
                <div className="absolute bottom-2 left-2 flex flex-row gap-2.5 z-10">
                  <label
                    htmlFor={uniqueId}
                    className="w-[24px] h-[22px] p-1 rounded-full bg-gray-0 justify-center items-center hover:bg-tertiary"
                  >
                    <div className="w-4 h-4">{icons.edit}</div>
                  </label>
                  <input id={uniqueId} type="file" onChange={onVideoChange} />
                  <Button
                    styleClass="w-[22px] h-[22px] p-1 rounded-full bg-gray-0 hover:bg-tertiary"
                    icon={icons.trash}
                    fullWidth
                    handleClick={resetVideo}
                    iconHeight="h-4"
                    iconWidth="w-4"
                  />
                </div>
              </div>
            )
          )}
          {videoUrl && (
            <>
              <div className="w-full">
                <video
                  className="w-full md:w-auto h-auto object-fill absolute top-0 max-h-full aspect-video rounded-xl overflow-hidden"
                  autoPlay={autoplay}
                  key={videoUrl}
                  onEnded={setFinished}
                  onPlay={setFinished}
                  ref={videoRef}
                  muted={false}
                  controls={true}
                  loop={true}
                >
                  <source src={videoUrl} type="video/mp4" />
                </video>
                {/* Custom Controls */}
              </div>{" "}
            </>
          )}
        </div>
        {error && (
          <div className="text-error-text text-right text-[10px] font-medium leading-4">
            {error && errorMessage}
          </div>
        )}
      </div>
      <div className="flex justify-center mt-2">
        <button
          onClick={togglePlayPause}
          className="bg-primary p-2 rounded-md text-white relative z-50"
        >
          {isPlaying ? "Pause" : "Play"}
        </button>
        <button
          onClick={handleFullscreen}
          className="bg-secondary p-2 rounded-md text-white"
        >
          Fullscreen
        </button>
      </div>
    </>
  );
};

export default VideoUploader;
